import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../state/AuthContext';
import { useTranslation } from 'react-i18next';
import { transformApiResponse } from '../utils/apiMiddleware';
import { ApiError } from '../lib/apiClient';
import AuthLayout from '../components/AuthLayout';
import CustomButton from '../components/CustomButton';

import type { Theme } from '@mui/material/styles';

const DEVICE_ID_KEY = 'licence_manager_device_id';

function getOrCreateDeviceId(): string {
  try {
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  } catch {
    return crypto.randomUUID();
  }
}

const getInputSx = (theme: Theme) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    fontSize: '1rem',
    '& fieldset': { borderColor: theme.palette.divider, borderWidth: '2px' },
    '&:hover fieldset': { borderColor: theme.palette.text.secondary },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
      borderWidth: '2px',
      boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`,
    },
    '&.Mui-disabled fieldset': { borderColor: theme.palette.divider, opacity: 0.5 },
  },
  '& .MuiInputLabel-root': { display: 'none' },
  '& .MuiInputBase-input': {
    padding: '0.75rem 1rem',
    '&::placeholder': { color: theme.palette.text.secondary, opacity: 1 },
  },
  '& .MuiInputBase-input.Mui-disabled': {
    WebkitTextFillColor: theme.palette.text.disabled,
  },
});

export default function LoginPage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { login, isAuthenticated, user, disabledMessage, clearDisabledMessage } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [licenseExpiredOpen, setLicenseExpiredOpen] = useState(false);
  const [licenseExpiredDate, setLicenseExpiredDate] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (!isAuthenticated || !user) return;
    const target = user.mustChangePassword ? '/force-password-change' : '/metrics';
    navigate(target, { replace: true });
  }, [isAuthenticated, user, navigate]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!username || !password) {
      setError(t('login.errors.missingCredentials'));
      return;
    }
    setError(null);
    setIsSubmitting(true);
    try {
      const deviceId = getOrCreateDeviceId();
      const response = await login({ username, password, deviceId });
      if (response.user.mustChangePassword) {
        navigate('/force-password-change', { replace: true });
      } else {
        navigate('/metrics', { replace: true });
      }
    } catch (err) {
      if (err instanceof ApiError && err.code === 'LICENSE_EXPIRED') {
        const expiresAt = err.details?.expiresAt || err.details?.details?.expiresAt;
        setLicenseExpiredDate(expiresAt || null);
        setLicenseExpiredOpen(true);
      } else {
        const friendlyError = transformApiResponse(err, t);
        setError(friendlyError.message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <AuthLayout subtitle={t('login.subtitle')}>
        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={2}>
            {error && (
              <Alert severity="error" sx={{ borderRadius: '8px' }}>
                {error}
              </Alert>
            )}

            <Stack spacing={0.5}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: theme.palette.text.secondary, fontSize: '0.9375rem' }}
              >
                {t('login.username')}
              </Typography>
              <TextField
                placeholder={t('login.usernamePlaceholder', 'Digite seu usuário ou e-mail')}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                autoComplete="username"
                disabled={isSubmitting}
                fullWidth
                inputProps={{ 'aria-label': t('login.username') }}
                sx={getInputSx(theme)}
              />
            </Stack>

            <Stack spacing={0.5}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: theme.palette.text.secondary, fontSize: '0.9375rem' }}
              >
                {t('login.password')}
              </Typography>
              <TextField
                placeholder={t('login.passwordPlaceholder', 'Digite sua senha')}
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                disabled={isSubmitting}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={
                          showPassword
                            ? t('login.hidePassword', 'Ocultar senha')
                            : t('login.showPassword', 'Mostrar senha')
                        }
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 36,
                          height: 36,
                          border: 'none',
                          background: 'none',
                          cursor: 'pointer',
                          padding: 0,
                          color: 'inherit',
                        }}
                      >
                        {showPassword ? (
                          <VisibilityOff fontSize="small" />
                        ) : (
                          <Visibility fontSize="small" />
                        )}
                      </button>
                    </InputAdornment>
                  ),
                }}
                inputProps={{ 'aria-label': t('login.password') }}
                sx={getInputSx(theme)}
              />
            </Stack>

            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={isSubmitting || !username.trim() || !password.trim()}
              fullWidth
              sx={{
                py: 1.5,
                mt: 0.5,
                borderRadius: '8px',
                fontWeight: 600,
                fontSize: '1rem',
                textTransform: 'none',
                background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                boxShadow: '0 4px 14px rgba(21, 101, 192, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                  boxShadow: '0 6px 20px rgba(21, 101, 192, 0.4)',
                },
                '&:disabled': {
                  background: (theme) =>
                    theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
                  boxShadow: 'none',
                },
              }}
            >
              {isSubmitting ? t('login.submitting') : t('login.submit')}
            </Button>

            <Box textAlign="center">
              <Button
                component="a"
                href="/forgot-password"
                variant="text"
                size="small"
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.8125rem',
                  color: theme.palette.text.secondary,
                  '&:hover': {
                    color: '#1565c0',
                    backgroundColor: 'transparent',
                  },
                }}
              >
                {t('login.forgotPassword', 'Esqueceu sua senha?')}
              </Button>
            </Box>
          </Stack>
        </Box>
      </AuthLayout>

      <Dialog
        open={Boolean(disabledMessage)}
        onClose={clearDisabledMessage}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ color: 'error.main' }}>
          {t('login.disabledDialog.title', 'Conta Desativada')}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {disabledMessage ||
              t(
                'login.disabledDialog.message',
                'Sua conta foi desativada. Entre em contato com o administrador.',
              )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <CustomButton onClick={clearDisabledMessage} variant="primary">
            {t('common.ok', 'OK')}
          </CustomButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={licenseExpiredOpen}
        onClose={() => setLicenseExpiredOpen(false)}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ color: 'error.main' }}>
          {t('login.licenseExpiredDialog.title', 'Licença Expirada')}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography>
              {t(
                'login.licenseExpiredDialog.message',
                'Sua licença expirou. Entre em contato com sua revenda para renovar a assinatura.',
              )}
            </Typography>
            {licenseExpiredDate && (
              <Typography variant="body2" color="text.secondary">
                {t('login.licenseExpiredDialog.expiredOn', 'Expirou em')}:{' '}
                {new Date(licenseExpiredDate).toLocaleDateString()}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <CustomButton onClick={() => setLicenseExpiredOpen(false)} variant="secondary">
            {t('common.ok', 'OK')}
          </CustomButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
